import { Headphones, Loader2 } from 'lucide-react'
import { Button } from '#app/components/ui/button.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useSoftphone, type SoftphoneStatus } from './softphone-provider.tsx'

/**
 * « Appels dans le navigateur » switch — shared by the header presence
 * sheet and the agent's profile page. Enabling asks for the microphone,
 * registers the Twilio Device and starts the presence heartbeat; from then
 * on inbound calls ring here first (the mobile is the fallback).
 */

const STATUS_LABEL: Record<SoftphoneStatus, string> = {
	off: 'Arrêté — les appels sonnent sur votre cellulaire',
	pending: 'Touchez l’écran pour reprendre les appels ici',
	starting: 'Démarrage…',
	ready: 'Connecté — les appels sonnent ici en premier',
	reconnecting: 'Reconnexion… — les appels sonnent ici, puis au cellulaire',
	error: 'Indisponible — les appels sonnent sur votre cellulaire',
}

const DOT_CLASS: Record<SoftphoneStatus, string> = {
	off: 'bg-muted-foreground/40',
	pending: 'bg-amber-500 animate-pulse',
	starting: 'bg-sky-500 animate-pulse',
	ready: 'bg-green-600',
	reconnecting: 'bg-amber-500 animate-pulse',
	error: 'bg-red-500',
}

export function SoftphoneToggle({ className }: { className?: string }) {
	const softphone = useSoftphone()
	if (!softphone.available) return null

	const { status, error, call } = softphone
	const on =
		status === 'ready' || status === 'starting' || status === 'reconnecting'
	// Only the initial start (mic prompt, token, registration) and a call in
	// progress lock the button: an agent must always be able to stop a
	// softphone that is reconnecting.
	const busy = status === 'starting' || call !== null

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			<div className="flex items-start gap-3">
				<span
					className={cn(
						'mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
						DOT_CLASS[status],
					)}
					aria-hidden="true"
				/>
				<div className="min-w-0 flex-1">
					<p className="flex items-center gap-2 text-sm font-semibold">
						<Headphones className="h-4 w-4 shrink-0" aria-hidden="true" />
						Appels dans le navigateur
					</p>
					<p className="text-muted-foreground text-xs">
						{call && on ? 'Connecté — en appel' : STATUS_LABEL[status]}
					</p>
					{error && (
						<p role="alert" className="text-destructive mt-1 text-xs">
							{error}
						</p>
					)}
				</div>
			</div>
			<Button
				type="button"
				variant={on ? 'outline' : 'secondary'}
				className="h-12 w-full"
				disabled={busy}
				aria-pressed={on}
				onClick={() =>
					on ? void softphone.disable() : void softphone.enable()
				}
			>
				{status === 'starting' ? (
					<>
						<Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
						Connexion…
					</>
				) : on ? (
					'Arrêter le softphone'
				) : status === 'error' ? (
					'Réessayer'
				) : status === 'pending' ? (
					'Reprendre le softphone'
				) : (
					'Activer le softphone'
				)}
			</Button>
			{call && on && (
				<p className="text-muted-foreground text-xs">
					Raccrochez d’abord pour arrêter le softphone.
				</p>
			)}
		</div>
	)
}
